
import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, BarChart3, Users, Clock, FileText, CheckCircle2 } from 'lucide-react';

const features = [
  {
    icon: <MessageSquare className="w-6 h-6 text-orcsys-500" />,
    title: "Communication directe",
    description: "Échangez directement avec notre équipe techniques sans intermédiaire."
  },
  {
    icon: <BarChart3 className="w-6 h-6 text-orcsys-500" />,
    title: "Suivi en temps réel",
    description: "Visualisez l'avancement de votre projet en temps réel."
  },
  {
    icon: <Clock className="w-6 h-6 text-orcsys-500" />,
    title: "Planning et délais",
    description: "Accédez aux échéanciers, livrables et documents du projet."
  },
  {
    icon: <FileText className="w-6 h-6 text-orcsys-500" />,
    title: "Documents centralisés",
    description: "Tous vos documents et communications sont centralisés au même endroit."
  }
];

const ProjectTrackingSection = () => {
  // Animation variants
  const containerVariant = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  
  const itemVariant = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 }
    }
  };
  
  return (
    <section className="py-20 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background patterns */}
      <div className="absolute top-0 left-0 w-full h-64 bg-[radial-gradient(circle_at_30%_20%,rgba(142,227,205,0.15)_0%,rgba(255,255,255,0)_60%)]"></div>
      <div className="absolute bottom-0 right-0 w-full h-64 bg-[radial-gradient(circle_at_70%_80%,rgba(155,135,245,0.15)_0%,rgba(255,255,255,0)_60%)]"></div>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmOGZhZmMiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRjMC0xLjEuOS0yIDItMmgxMmMxLjEgMCAyIC45IDIgMnYxMmMwIDEuMS0uOSAyLTIgMkgzOGMtMS4xIDAtMi0uOS0yLTJWMzR6TTQwIDQwYzAtMS4xLjktMiAyLTJoMTJjMS4xIDAgMiAuOSAyIDJ2MTJjMCAxLjEtLjkgMi0yIDJINDJjLTEuMSAwLTItLjktMi0yVjQweiIvPjwvZz48L2c+PC9zdmc+')] opacity-30"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div 
            className="order-2 lg:order-1"
            variants={containerVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.h2 
              variants={itemVariant} 
              className="text-3xl md:text-4xl font-bold mb-6 text-slate-900 leading-tight"
            >
              Suivez vos projets <span className="text-orcsys-600">en temps réel</span>
            </motion.h2>
            
            <motion.p 
              variants={itemVariant}
              className="text-lg text-slate-600 mb-8"
            >
              L'interface utilisateur intuitive de Collab simplifie la communication, 
              la rendant accessible aux utilisateurs de tous niveaux techniques.
            </motion.p>
            
            <div className="space-y-6">
              {features.map((feature, index) => (
                <motion.div 
                  key={index} 
                  variants={itemVariant}
                  className="flex items-start gap-4 group"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-orcsys-50 rounded-xl flex items-center justify-center group-hover:bg-orcsys-100 transition-colors">
                    {feature.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-orcsys-600 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            
            {/* Badges */}
            <motion.div 
              variants={itemVariant}
              className="flex flex-wrap gap-3 mt-8"
            >
              {["Rapports", "Graphiques", "Notifications", "Commentaires", "Tâches"].map((tag, index) => (
                <span 
                  key={index}
                  className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
            </motion.div>
          </motion.div>

          {/* Device Mockup */}
          <motion.div 
            className="order-1 lg:order-2 relative"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <div className="relative mx-auto w-full max-w-[600px] md:max-w-[700px]">
              <div className="absolute inset-0 bg-gradient-to-r from-orcsys-100 to-orcsys-200 rounded-xl blur-xl opacity-50 animate-pulse-soft"></div>
              <div className="relative bg-white rounded-xl shadow-xl overflow-hidden border border-gray-100">
                {/* Screen Content - Project Dashboard */}
                <div className="aspect-[16/12] bg-gradient-to-br from-slate-50 to-white">
                  <img 
                    src="images/collab-project-desktop.png" 
                    alt="Collab dashboard"
                    className="w-full h-full object-fill rounded-lg shadow-md"
                  />
                </div>
                {/* Base */}
                <div className="h-3 md:h-4 bg-slate-100 "></div>
              </div>
            </div>

            {/* Phone Mockup */}
            <motion.div 
              className="absolute -bottom-4 md:bottom-2 -right-4 md:-right-8 w-32 h-64 md:w-48 md:h-96"
              initial={{ opacity: 0, x: 20, y: 20 }}
              whileInView={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-orcsys-100 to-orcsys-200 rounded-xl blur-xl opacity-50 animate-pulse-soft"></div>
                <div className="relative bg-white rounded-[1.5rem] md:rounded-[2rem] shadow-xl overflow-hidden border border-gray-100">
                  {/* Phone Notch */}
                  <div className="h-4 md:h-6 bg-slate-900 rounded-t-[1.5rem] md:rounded-t-[2rem] flex items-center justify-center">
                    <div className="w-16 md:w-20 h-3 md:h-4 bg-slate-800 rounded-full"></div>
                  </div>
                  {/* Phone Screen - Mobile Project View */}
                  <div className="aspect-[9/18] bg-gradient-to-br from-orcsys-50 to-white overflow-hidden">
                    <img 
                      src="images/collab-project-mobile.jpg" 
                      alt="Collab mobile app"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Phone Home Button */}
                  <div className="h-8 md:h-12 bg-slate-100 border-t border-gray-200 flex items-center justify-center">
                    <div className="w-8 md:w-12 h-1 bg-slate-300 rounded-full"></div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProjectTrackingSection;
